// pages/Billing.jsx
import React, { useState, useEffect } from 'react';
import { 
CreditCard, TrendingUp, History, Package, ArrowRight, 
DollarSign, Calendar, AlertCircle, CheckCircle, ChevronRight, Home, Zap 
} from 'lucide-react';
import Header from '@/components/ui/Header';
import CurrentPlan from './components/CurrentPlan';
import UsageStats from './components/UsageStats';
import PlanComparison from './components/PlanComparison';
import TransactionHistory from './components/TransactionHistory';
import ConfirmationStatusModal from './components/ConfirmationStatusModal';

import apiRequest from '@/services/requests';

const TABS = [
{ 
id: 'overview', 
label: 'Overview', 
icon: Package,
description: 'Current plan & usage'
},
{ 
id: 'plans', 
label: 'Plans & Pricing', 
icon: TrendingUp,
description: 'Upgrade or change plan'
},
{ 
id: 'history', 
label: 'Transaction History', 
icon: History,
description: 'Past payments & invoices'
},
];

const Billing = () => {
const [activeTab, setActiveTab] = useState('overview');
const [billingInfo, setBillingInfo] = useState(null);
const [usageStats, setUsageStats] = useState(null);
const [loading, setLoading] = useState(true);
const [isTransitioning, setIsTransitioning] = useState(false);
const [isMobile, setIsMobile] = useState(false);
const [showModal, setShowModal] = useState(false);
const [modalData, setModalData] = useState(null);
const [upgrading, setUpgrading] = useState(null);
const [packages, setPackages] = useState(null);


useEffect(() => {
loadData();
const checkMobile = () => setIsMobile(window.innerWidth < 768);
checkMobile();
window.addEventListener('resize', checkMobile);
return () => window.removeEventListener('resize', checkMobile);
}, []);


useEffect(() => {
// Listen for payment completion from callback
const urlParams = new URLSearchParams(window.location.search);
const status = urlParams.get('status');
const txRef = urlParams.get('tx_ref');

if (status && txRef && txRef.startsWith('UPG')) {
if (status === 'success') {
showSuccessModal('Package updated successfully!');
// Clean URL
window.history.replaceState({}, document.title, window.location.pathname);
// Refresh data
loadData();
} else if (status === 'failed') {
showErrorModal('Payment failed. Please try again.');
window.history.replaceState({}, document.title, window.location.pathname);
}
}
}, []);



const loadData = async () => {
setLoading(true);
try {
const [billingRes, usageRes, packagesRes] = await Promise.all([
apiRequest.getBillingInfo(),
apiRequest.getUsageStats(),
apiRequest.getPackageFeatures()
]);

if (billingRes.success) {
setBillingInfo(billingRes.data);
}
if (usageRes.success) {
setUsageStats(usageRes.data);
}

if (packagesRes.success) {
setPackages(packagesRes.data.allPackages);
}

} catch (error) {
console.error('Error loading billing data:', error);
}
setLoading(false);
};



const handleTabChange = (tabId) => {
setIsTransitioning(true);
setTimeout(() => {
setActiveTab(tabId);
setIsTransitioning(false);
window.scrollTo({ top: 0, behavior: 'smooth' });
}, 150);
};

const renderTabContent = () => {
const content = (() => {
switch (activeTab) {
case 'overview':
return (
<div className="space-y-6">
<CurrentPlan billingInfo={billingInfo} onUpdate={loadData} />
<UsageStats usageStats={usageStats} />
</div>
);
case 'plans':
return <PlanComparison currentPackage={billingInfo?.package} onUpgrade={loadData} handleUpgrade={handleUpgrade} upgrading={upgrading} loading={loading}
packages={packages} />;
case 'history':
return <TransactionHistory />;
default:
return null;
}
})();

return (
<div 
className={`transition-all duration-150 ${
isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
}`}
>
{content}
</div>
);
};

const packageEmoji = {
standard: '⭐',
premium: '💎',
elite: '👑'
};

const activeTabData = TABS.find(tab => tab.id === activeTab);


const handleUpgrade = async (packageType) => {
if (packageType === billingInfo?.package) return;

const pkg = packages[packageType];
const isUpgrade = pkg.price > packages[billingInfo?.package].price;
const action = isUpgrade ? 'upgrade to' : 'downgrade to';

// Show confirmation modal
setModalData({
type: 'confirm',
packageType,
packageName: pkg.name,
price: pkg.price,
isUpgrade,
action
});
setShowModal(true);
};

const confirmUpgrade = async () => {
const packageType = modalData.packageType;

setShowModal(false);
setUpgrading(packageType);

try {
// Call upgrade endpoint
const res = await apiRequest.upgradePackage(packageType);

if (res.success) {
// Redirect to payment
window.location.href = res.data.checkoutUrl;
}
} catch (error) {
console.error('Error initiating upgrade:', error);
showErrorModal(
error.response?.data?.message || 'Failed to initiate package change. Please try again.'
);
setUpgrading(null);
}
};

const showSuccessModal = (message) => {
setModalData({
type: 'success',
message
});
setShowModal(true);
setTimeout(() => setShowModal(false), 3000);
};

const showErrorModal = (message) => {
setModalData({
type: 'error',
message
});
setShowModal(true);
};


return (
<div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
<Header />

<main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8">
<div className="max-w-7xl mx-auto">
{/* Breadcrumb */}
{/*<div className="flex items-center gap-2 text-sm text-slate-600 mb-4">
<Home size={16} />
<ChevronRight size={14} />
<span>Billing</span>
{activeTabData && (
<>
<ChevronRight size={14} />
<span className="text-purple-600 font-medium">{activeTabData.label}</span>
</>
)}
</div>*/}

{/* Enhanced Header */}
<div className="mb-8">
<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
<div className="flex items-center gap-4">
<div className="relative">
<div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-xl">
<CreditCard className="text-white" size={16} />
</div>
{/* Pulse animation */}
<div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl animate-pulse opacity-20"></div>
</div>
<div>
<h1 className="text-xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
Billing & Subscription
</h1>
<p className="text-text-secondary text-sm mt-1">
Manage your plan and payment methods
</p>
</div>
</div>

{/* Quick Stats - Only show when data is loaded */}
{!loading && billingInfo && (
<div className="flex gap-3">
<div className="bg-white rounded-xl border border-slate-200 px-4 py-3 shadow-sm">
<p className="text-xs text-slate-600 mb-1">Current Plan</p>
<div className="flex items-center gap-2">
<span className="text-md">{packageEmoji[billingInfo.package]}</span>
<p className="font-bold text-slate-900 capitalize">
{billingInfo.package}
</p>
</div>
</div>

{billingInfo.package_status === 'active' && billingInfo.daysUntilExpiry && (
<div className={`rounded-xl border px-4 py-3 shadow-sm ${
billingInfo.daysUntilExpiry <= 7 
? 'bg-amber-50 border-amber-200' 
: 'bg-green-50 border-green-200'
}`}>
<p className="text-xs text-slate-600 mb-1">Expires In</p>
<div className="flex items-center gap-2">
<Calendar size={16} className={
billingInfo.daysUntilExpiry <= 7 ? 'text-amber-600' : 'text-green-600'
} />
<p className={`font-bold ${
billingInfo.daysUntilExpiry <= 7 ? 'text-amber-900' : 'text-green-900'
}`}>
{billingInfo.daysUntilExpiry} days
</p>
</div>
</div>
)}
</div>
)}
</div>
</div>

{/* Tab Navigation - Enhanced */}
<div className="mb-8">
<div className={`
${isMobile 
? 'flex flex-col gap-2' 
: 'flex gap-2 p-1.5 bg-white rounded-xl shadow-sm border border-slate-200'
}
`}>
{TABS.map((tab, index) => {
const Icon = tab.icon;
const isActive = activeTab === tab.id;

return (
<button
key={tab.id}
onClick={() => handleTabChange(tab.id)}
className={`
flex items-center gap-3 px-6 py-4 rounded-xl font-semibold transition-all duration-200
${isMobile ? 'w-full justify-between' : ''}
${isActive 
? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg transform scale-105' 
: 'text-slate-600 hover:bg-slate-50 bg-white border border-slate-200'
}
`}
style={{
animationDelay: `${index * 50}ms`
}}
>
<div className="flex items-center gap-3">
<Icon size={20} />
<div className="text-left">
<p className={`font-bold ${isActive ? 'text-white' : 'text-slate-900'}`}>
{tab.label}
</p>
{isMobile && (
<p className={`text-xs ${isActive ? 'text-white/80' : 'text-slate-500'}`}>
{tab.description}
</p>
)}
</div>
</div>
{isActive && isMobile && <ChevronRight size={20} />}
</button>
);
})}
</div>

{/* Active tab description for desktop */}
{!isMobile && activeTabData && (
<div className="mt-4 flex items-center gap-2 text-sm text-slate-600 bg-white px-4 py-3 rounded-xl border border-slate-200 shadow-sm">
<Zap size={16} className="text-indigo-600" />
<span>{activeTabData.description}</span>
</div>
)}
</div>

{/* Content */}
{loading ? (
<div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200">
<div className="relative">
<div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600"></div>
<div className="absolute inset-0 flex items-center justify-center">
<CreditCard className="text-indigo-300" size={24} />
</div>
</div>
<p className="text-slate-600 mt-4 font-medium">Loading billing information...</p>
</div>
) : (
renderTabContent()
)}

{/* Trust Badges Footer */}
<div className="mt-8 p-6 bg-white rounded-xl border border-slate-200 shadow-sm">
<div className="flex flex-col md:flex-row items-center justify-between gap-4">
<div className="flex items-center gap-3">
<div className="p-2 bg-green-100 rounded-lg">
<CheckCircle className="text-green-600" size={24} />
</div>
<div>
<p className="font-semibold text-slate-900">Secure Payment Processing</p>
<p className="text-xs text-slate-600">Your payment information is encrypted and secure</p>
</div>
</div>

<div className="flex items-center gap-4 text-xs text-slate-500">
<span className="flex items-center gap-2">
<span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
PCI Compliant
</span>
<span>•</span>
<span>256-bit SSL</span>
<span>•</span>
<span>Money Back Guarantee</span>
</div>
</div>
</div>
</div>


{/* Confirmation/Status Modal */}
{showModal && (
<ConfirmationStatusModal
confirmUpgrade={confirmUpgrade} 
modalData={modalData} 
setShowModal={setShowModal} 
/>
)}





</main>
</div>
);
};

export default Billing;